import { useState, useRef, useEffect } from "react";

/**
 * Custom React hook to detect user presence:
 * - isIdle: true if user inactive for timeout ms
 * - isVisible: true if page is visible
 * - isActive: true if user is not idle AND page is visible
 *
 * @param {number} timeout - Inactivity timeout in milliseconds (default 60s)
 */
export function useUserPresence(timeout = 60000) {
  const [isIdle, setIsIdle] = useState(false);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const timer = useRef();

  // Reset idle timer on user interaction
  useEffect(() => {
    const resetTimer = () => {
      setIsIdle(false);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setIsIdle(true), timeout);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer(); // Initial start

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timer.current);
    };
  }, [timeout]);

  // Listen for page visibility change
  useEffect(() => {
    const handleVisibility = () => setIsVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return {
    isIdle,
    isVisible,
    isActive: !isIdle && isVisible,
  };
}